export type LayoutProfile = 'sales_rep' | 'sales_manager' | 'collections' | 'dispatch' | 'superadmin';

export interface LayoutVisibility {
  attentionBrief: boolean;
  outlook: boolean;
  myTasks: boolean;
  approvals: boolean;
  /** Cash: overdue + due today + due soon queues */
  cashQueues: boolean;
  ordersAtRisk: boolean;
  pendingWorkOrder: boolean;
  deliveries: boolean;
  crmFollowUps: boolean;
  quotationConversion: boolean;
  /** Legacy registry tables — off by default on redesigned tower */
  pendingReview: boolean;
  ordersInProgress: boolean;
  teamPipeline: boolean;
  quickActions: boolean;
  draftCounts: boolean;
  kpiTrends: boolean;
  /** @deprecated use cashQueues */
  arTiles: boolean;
  /** @deprecated use cashQueues */
  arCollections: boolean;
}

export function getSalesControlTowerLayout(user: {
  is_super_admin?: boolean;
  permissions?: string[];
  preferences?: string | Record<string, unknown> | null;
} | null): { profile: LayoutProfile; visibility: LayoutVisibility } {
  let override: LayoutProfile | undefined;
  if (user?.preferences) {
    try {
      const prefs =
        typeof user.preferences === 'string' ? JSON.parse(user.preferences) : user.preferences;
      const raw = prefs?.sales_control_tower_layout;
      if (
        raw === 'sales_rep' ||
        raw === 'sales_manager' ||
        raw === 'collections' ||
        raw === 'dispatch' ||
        raw === 'superadmin'
      ) {
        override = raw;
      }
    } catch {
      /* ignore */
    }
  }

  const perms = user?.permissions ?? [];
  const isSuper = Boolean(user?.is_super_admin);
  const hasFinance = perms.some((p) => p.startsWith('finance.'));
  const hasSalesViewAll = perms.includes('growth.team.view_all') || isSuper;

  let profile: LayoutProfile = override ?? 'sales_manager';
  if (!override) {
    if (isSuper) profile = 'superadmin';
    else if (hasFinance && !perms.some((p) => p.startsWith('sales.'))) profile = 'collections';
    else if (perms.includes('growth.delivery.view') && !hasSalesViewAll) profile = 'dispatch';
    else if (!hasSalesViewAll) profile = 'sales_rep';
  }

  const base: LayoutVisibility = {
    attentionBrief: true,
    outlook: true,
    myTasks: true,
    approvals: true,
    cashQueues: true,
    ordersAtRisk: true,
    pendingWorkOrder: true,
    deliveries: true,
    crmFollowUps: true,
    quotationConversion: true,
    pendingReview: false,
    ordersInProgress: false,
    teamPipeline: false,
    quickActions: false,
    draftCounts: false,
    kpiTrends: false,
    arTiles: true,
    arCollections: true,
  };

  switch (profile) {
    case 'sales_rep':
      return {
        profile,
        visibility: {
          ...base,
          cashQueues: false,
          arTiles: false,
          arCollections: false,
          teamPipeline: false,
          kpiTrends: false,
        },
      };
    case 'collections':
      return {
        profile,
        visibility: {
          ...base,
          myTasks: false,
          ordersAtRisk: false,
          pendingWorkOrder: false,
          deliveries: false,
          crmFollowUps: false,
          quotationConversion: false,
          teamPipeline: false,
          outlook: true,
          attentionBrief: true,
          cashQueues: true,
        },
      };
    case 'dispatch':
      return {
        profile,
        visibility: {
          ...base,
          cashQueues: false,
          arTiles: false,
          arCollections: false,
          crmFollowUps: false,
          quotationConversion: false,
          teamPipeline: false,
          kpiTrends: false,
          pendingWorkOrder: true,
          ordersAtRisk: true,
          deliveries: true,
          outlook: true,
        },
      };
    case 'superadmin':
    case 'sales_manager':
    default:
      return { profile, visibility: base };
  }
}

export function getRefreshIntervalMs(user: { preferences?: string | Record<string, unknown> | null } | null): number | false {
  if (!user?.preferences) return false;
  try {
    const prefs =
      typeof user.preferences === 'string' ? JSON.parse(user.preferences) : user.preferences;
    const mins = Number(prefs?.sales_tower_refresh_minutes);
    if (mins === 5 || mins === 15 || mins === 30) return mins * 60 * 1000;
  } catch {
    /* ignore */
  }
  return false;
}
